/**
 * 媒体资源 - 按 AI 分析轮次（即"第 N 次提交"）分组展示
 * 缩略图点击 → 专业 Lightbox 预览（全屏 / 缩放 / 旋转 / 上下张切换 / 键盘 Esc·←·→）
 */
import React, { useCallback, useMemo, useState } from 'react'
import { PlayCircleOutlined } from '@ant-design/icons'
import { AiAnalysisItem, CheckMediaItem } from '../api/check'
import MediaLightbox, { detectIsVideo } from '../shared/MediaLightbox'
import { formatRoundLabel } from './utils'

interface MediaResourcesProps {
  rounds: AiAnalysisItem[]
}

interface FlatMedia extends CheckMediaItem {
  /** 所属轮次（"第 N 次提交"） */
  roundLabel: string
  roundIndex: number
}

const VersionTag: React.FC<{
  label: string
  tone: 'blue' | 'slate' | 'green'
}> = ({ label, tone }) => (
  <span
    className={`myse-soft-tag myse-soft-tag--${tone}`}
    style={{
      height: 22,
      padding: '0 8px',
      fontWeight: 500,
      lineHeight: '22px'
    }}
  >
    {label}
  </span>
)

const MediaThumb: React.FC<{
  item: CheckMediaItem
  onPreview: () => void
}> = ({ item, onPreview }) => {
  const [hover, setHover] = useState(false)
  return (
    <div
      onClick={onPreview}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        width: 132,
        height: 99,
        borderRadius: 8,
        overflow: 'hidden',
        background: '#1F252F',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'box-shadow .15s, transform .15s',
        boxShadow: hover ? '0 4px 12px rgba(0,0,0,.18)' : 'none',
        transform: hover ? 'translateY(-1px)' : 'none'
      }}
    >
      {detectIsVideo(item) ? (
        <>
          <video
            src={item.photo_url}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            preload="metadata"
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff'
            }}
          >
            <PlayCircleOutlined style={{ fontSize: 28 }} />
          </div>
        </>
      ) : (
        <img
          src={item.photo_url}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <span
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          fontSize: 10,
          color: '#fff',
          background: 'rgba(0,0,0,0.5)',
          padding: '1px 6px',
          borderRadius: 3,
          fontFamily: 'Inter, system-ui',
          fontWeight: 600
        }}
      >
        #{item.photo_seq}
      </span>
    </div>
  )
}

const MediaResources: React.FC<MediaResourcesProps> = ({ rounds }) => {
  const [previewIndex, setPreviewIndex] = useState<number>(-1)

  // 扁平化所有媒体（跨 round 切换）
  const flatList = useMemo<FlatMedia[]>(() => {
    const arr: FlatMedia[] = []
    rounds.forEach((round, idx) => {
      const label = formatRoundLabel(idx)
      ;(round.media_list || []).forEach((m) => {
        arr.push({ ...m, roundLabel: label, roundIndex: idx })
      })
    })
    return arr
  }, [rounds])

  const handlePrev = useCallback(() => {
    setPreviewIndex((i) => (i > 0 ? i - 1 : i))
  }, [])
  const handleNext = useCallback(() => {
    setPreviewIndex((i) => (i < flatList.length - 1 ? i + 1 : i))
  }, [flatList.length])

  // 全局 flat 序号映射 (photo_seq -> flat index)
  // 注意：所有 Hook 都需在条件分支前调用，避免 rules-of-hooks
  const indexByKey = useMemo(() => {
    const map = new Map<string, number>()
    flatList.forEach((m, i) => map.set(`${m.roundIndex}:${m.photo_seq}`, i))
    return map
  }, [flatList])

  const totalCount = flatList.length

  if (totalCount === 0) {
    return (
      <div
        style={{
          padding: '24px 0',
          color: '#9CA3AF',
          fontSize: 13,
          textAlign: 'center'
        }}
      >
        暂无媒体资源
      </div>
    )
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {rounds.map((round, idx) => {
          const isLast = idx === rounds.length - 1
          const label = formatRoundLabel(idx)
          return (
            <div key={round.ai_analysis_id || idx}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                  flexWrap: 'wrap'
                }}
              >
                <VersionTag label={label} tone={isLast ? 'blue' : 'slate'} />
                <span
                  style={{
                    fontSize: 12,
                    color: '#6B7280',
                    fontFamily: 'Inter, system-ui'
                  }}
                >
                  {round.create_time}
                </span>
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                  · {round.media_list?.length || 0} 张
                </span>
                {isLast && <VersionTag label="最新" tone="green" />}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {(round.media_list || []).map((m) => (
                  <MediaThumb
                    key={m.photo_seq}
                    item={m}
                    onPreview={() =>
                      setPreviewIndex(
                        indexByKey.get(`${idx}:${m.photo_seq}`) ?? 0
                      )
                    }
                  />
                ))}
                {(round.media_list || []).length === 0 && (
                  <span style={{ color: '#9CA3AF', fontSize: 12 }}>
                    本轮无媒体
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {previewIndex >= 0 && (
        <MediaLightbox
          list={flatList}
          index={previewIndex}
          onClose={() => setPreviewIndex(-1)}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}
    </>
  )
}

export default MediaResources
